import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { useUID } from 'react-uid';
import { twJoin, twMerge } from 'tailwind-merge';

import { noop } from 'lib/utils';

import Text from '../Text';

const RadioButton = forwardRef(
  (
    {
      checked = false,
      className,
      defaultChecked = false,
      disabled = false,
      id: customId,
      label,
      name,
      value,
      onChange = noop,
      onClick = noop,
      ...rest
    },
    ref
  ) => {
    const uniqueId = useUID();
    const id = customId || uniqueId;

    return (
      <div
        className={twMerge(
          'inline-flex select-none items-center text-neutral-low-100 dark:text-neutral-high-200',
          className
        )}
        {...rest}
      >
        <input
          ref={ref}
          checked={checked}
          className="peer absolute m-1.5 hidden size-px overflow-hidden whitespace-nowrap p-0"
          defaultChecked={defaultChecked}
          disabled={disabled}
          id={id}
          name={name}
          type="radio"
          value={value}
          suppressHydrationWarning
          onChange={(e) => disabled || onChange(e)}
          /**
           * Is used to suppress errors related of inconsistencies between
           * client-side and server-side of ids generated by 'react-uid'
           */
          onClick={onClick}
        />
        <Text
          as="label"
          className={twJoin(
            'relative pl-6 peer-checked:font-bold peer-checked:text-primary-300 dark:peer-checked:text-primary-200',
            'before:absolute before:-left-px before:top-1 before:mr-1 before:size-4 before:rounded-full before:border before:border-solid before:border-neutral-low-100 before:bg-neutral-high-100 before:transition-all before:duration-150 before:ease-in before:content-empty',
            'after:absolute after:left-[3px] after:top-1/3 after:block after:size-2 after:rounded-full after:bg-transparent after:transition-all after:duration-150 after:ease-in after:content-empty',
            'peer-checked:before:border-primary-300 peer-checked:after:bg-primary-300 peer-focus:before:shadow-[0_0_0_2px_theme(colors.neutral-high.100),0_0_0_1px_(theme(colors.primary.200))] dark:before:border-neutral-high-200 dark:before:bg-neutral-low-300 dark:peer-checked:before:border-primary-200 dark:peer-checked:after:bg-primary-200 dark:peer-focus:before:shadow-[0_0_0_1px_theme(colors.neutral-low.300),0_0_0_2px_theme(colors.primary.200)]',
            disabled &&
              checked &&
              'after:bg-neutral-high-300 dark:after:bg-neutral-low-100',
            disabled
              ? 'cursor-not-allowed text-neutral-high-500 before:border-neutral-high-500 dark:text-neutral-low-100 dark:before:border-neutral-high-500'
              : 'cursor-pointer'
          )}
          htmlFor={id}
        >
          {label}
        </Text>
      </div>
    );
  }
);

RadioButton.displayName = 'RadioButton';

RadioButton.propTypes = {
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Renders a checked radio button
   */
  checked: PropTypes.bool,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Default value for uncontrolled component
   */
  defaultChecked: PropTypes.bool,
  /**
   * Renders a disabled radio button
   */
  disabled: PropTypes.bool,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Custom identifier for the checkbox input. If this is not provided,
   *  we will add a unique id with the prefix `radio-button_`
   */
  id: PropTypes.string,
  /**
   * Text to be render as label
   */
  label: PropTypes.string.isRequired,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   *  Name of the radio button group
   */
  name: PropTypes.string,
  /**
   * String to use as the value of the radio button
   */
  value: PropTypes.string.isRequired,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback fn to be executed on `onChange` event
   */
  onChange: PropTypes.func,
  /**
   * **DEVELOPMENT USE ONLY**
   *
   * Callback fn to be executed on `onClick` event
   */
  onClick: PropTypes.func,
};

export default RadioButton;
